import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Top-level safety net — catches React render crashes anywhere in the tree
 * so the user always sees something useful instead of a blank/black page.
 *
 * In dev mode, react-error-overlay still shows the red detail screen; that's
 * fine and helpful. In production builds this is the user-facing fallback.
 */
export default class ErrorBoundary extends React.Component {
  state = { hasError: false, message: '', stack: '' };

  static getDerivedStateFromError(err) {
    return {
      hasError: true,
      message: err?.message || 'Something went wrong.',
      stack: err?.stack || '',
    };
  }

  componentDidCatch(err, info) {
    // Best-effort log so it shows up in the browser console; intentionally
    // does NOT call any analytics/Sentry hook — keep dependency surface small.
    if (typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary]', err, info?.componentStack);
    }
  }

  handleReload = () => {
    // Force a hard reload (skip cache) so a transient bundle issue doesn't loop.
    if (typeof window !== 'undefined') window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#FBF8F2] to-[#F0E8DD] px-4"
        data-testid="error-boundary-screen">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-stone-200 p-7 sm:p-9 text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-red-50 border border-red-200 flex items-center justify-center mb-4">
            <AlertTriangle className="w-7 h-7 text-red-600" strokeWidth={1.75} />
          </div>
          <h1 className="text-lg sm:text-xl font-bold text-[#3A1F12] mb-2">
            Something went wrong
          </h1>
          <p className="text-sm text-[#5C6B64] leading-relaxed mb-5">
            We hit an unexpected error while rendering this page. Reloading usually fixes it.
            If the problem persists, please contact support.
          </p>
          {this.state.message && (
            <div className="mb-5 p-3 bg-red-50 border border-red-100 rounded-md text-left">
              <div className="text-[10px] uppercase tracking-wider text-red-700 font-semibold mb-1">Error</div>
              <div className="text-xs text-red-800 font-mono break-words">{this.state.message}</div>
            </div>
          )}
          <Button
            className="w-full bg-[color:var(--color-primary)] hover:bg-[#1A4D3C] text-white"
            onClick={this.handleReload}
            data-testid="error-boundary-reload"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Reload page
          </Button>
        </div>
      </div>
    );
  }
}
